import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { SearchX } from "lucide-react";
import { Reveal } from "./Reveal";
import { PostCard } from "./PostCard";
import { MatchCard } from "./MatchCard";
import { CATEGORIES } from "./categories";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_FILTERS = [
  { value: "all", label: "All" },
  { value: "need", label: "Needs" },
  { value: "offer", label: "Offers" },
];

export const Opportunities = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState("all");
  const [category, setCategory] = useState("all");
  const [location, setLocation] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/matches`)
      .then((r) => setMatches(r.data))
      .catch(() => setMatches([]));
  }, []);

  useEffect(() => {
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const params = {};
        if (type !== "all") params.post_type = type;
        if (category !== "all") params.category = category;
        if (location.trim()) params.location = location.trim();
        const r = await axios.get(`${API}/posts`, { params });
        setPosts(r.data);
      } catch {
        setPosts([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [type, category, location]);

  return (
    <main data-testid="opportunities-page" className="relative min-h-screen pt-32 pb-24 bg-grid overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(ellipse 55% 35% at 50% 0%, rgba(0,240,255,0.06), transparent 65%)" }}
      />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <span className="font-mono-accent text-xs tracking-[0.3em] text-[#00F0FF]" data-testid="opportunities-eyebrow">
              /// THE BOARD
            </span>
            <h1 className="mt-5 font-display font-extrabold uppercase text-[clamp(1.3rem,5.8vw,1.75rem)] sm:text-4xl lg:text-5xl leading-[1.02] text-slate-100 break-words" data-testid="opportunities-title">
              Opportunities
            </h1>
            <p className="mt-5 max-w-xl text-slate-400 text-base sm:text-lg leading-relaxed">
              Live needs and offers from the network. Find the other side of your X.
            </p>
          </div>
          <button data-testid="opportunities-post-btn" onClick={() => navigate("/post")} className="btn-primary">
            Post to the X
          </button>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-12 flex flex-col lg:flex-row gap-4" data-testid="opportunities-filters">
            <div className="flex gap-2">
              {TYPE_FILTERS.map((t) => (
                <button
                  key={t.value}
                  data-testid={`opportunities-filter-${t.value}`}
                  onClick={() => setType(t.value)}
                  className={`flex-1 sm:flex-none px-5 py-3.5 sm:py-3 font-mono-accent text-[11px] uppercase tracking-[0.2em] border transition-all duration-300 ${
                    type === t.value
                      ? "border-[#00F0FF] text-[#00F0FF] bg-[#00F0FF]/5"
                      : "border-white/10 text-slate-500 hover:border-white/30 hover:text-slate-300"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <select
              data-testid="opportunities-category-filter"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="field-input appearance-none cursor-pointer lg:w-64"
            >
              <option value="all" className="bg-[#0A0D14]">All Categories</option>
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value} className="bg-[#0A0D14]">
                  {c.label}
                </option>
              ))}
            </select>
            <input
              data-testid="opportunities-location-filter"
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Filter by location…"
              className="field-input lg:flex-1"
            />
          </div>
        </Reveal>

        {matches.length > 0 && (
          <Reveal delay={0.12} className="mt-12" >
            <div data-testid="matches-section">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <span className="font-mono-accent text-xs tracking-[0.3em] text-[#00F0FF]">/// AUTO-MATCHED</span>
                <span className="font-mono-accent text-[10px] tracking-[0.25em] text-slate-600 uppercase">
                  {matches.length} {matches.length === 1 ? "match" : "matches"} found on the board
                </span>
              </div>
              <div className="mt-6 grid lg:grid-cols-2 gap-5">
                {matches.map((m) => (
                  <MatchCard key={m.id} match={m} />
                ))}
              </div>
            </div>
          </Reveal>
        )}

        <div className="mt-10" data-testid="opportunities-grid">
          {loading ? (
            <p className="py-20 text-center font-mono-accent text-xs tracking-[0.3em] text-slate-600" data-testid="opportunities-loading">
              SCANNING THE NETWORK…
            </p>
          ) : posts.length === 0 ? (
            <div className="card-frame py-20 px-8 text-center" data-testid="opportunities-empty">
              <SearchX size={28} className="mx-auto text-slate-600" />
              <h2 className="mt-6 font-display font-bold uppercase text-xl text-slate-200">
                Nothing at this intersection yet
              </h2>
              <p className="mt-3 text-sm text-slate-500 max-w-sm mx-auto leading-relaxed">
                No posts match these filters. Adjust them — or be the first to post.
              </p>
              <button data-testid="opportunities-empty-post-btn" onClick={() => navigate("/post")} className="btn-ghost mt-8 !px-6 !py-3">
                Post to the X
              </button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {posts.map((p) => (
                <PostCard key={p.id} post={p} />
              ))}
            </div>
          )}
        </div>

        {!loading && posts.length > 0 && (
          <p className="mt-8 font-mono-accent text-[10px] tracking-[0.3em] text-slate-600" data-testid="opportunities-count">
            {posts.length} {posts.length === 1 ? "POST" : "POSTS"} ON THE BOARD
          </p>
        )}
      </div>
    </main>
  );
};
